// =============================================================================
// features/documents/components/DocumentCard.tsx — Card de documento (F46-S08).
//
// Mostra: label do tipo, badge de status (scan+revisão), nome de arquivo em
// mono truncado, tamanho, data, origem. Download some sem `documents:download`;
// excluir some sem `documents:delete` — nunca assume que quem lista pode
// baixar. DS: card elev-2, hover Spotlight (halo azul via --mx/--my), mesmo
// padrão de features/contracts/WinbackTaskCard.tsx.
// =============================================================================

import type { Document } from '@elemento/shared-schemas';
import * as React from 'react';

import type { BadgeVariant } from '../../../components/ui/Badge';
import { Badge } from '../../../components/ui/Badge';
import { cn } from '../../../lib/cn';
import { formatOrgDate } from '../../../lib/orgDateTime';

import { DocumentStatusBadge } from './DocumentStatusBadge';

// ─── Helpers puros (testados em __tests__/DocumentCard.test.ts) ────────────

export interface OriginBadgePresentation {
  label: string;
  variant: BadgeVariant;
}

/** Badge de origem a partir dos vínculos (docs/planejamento-documentos-anexos.md §6.1). */
export function resolveOriginBadge(
  doc: Pick<Document, 'analysis_id' | 'contract_id' | 'message_id'>,
): OriginBadgePresentation {
  if (doc.analysis_id) return { label: 'Análise', variant: 'info' };
  if (doc.contract_id) return { label: 'Contrato', variant: 'info' };
  if (doc.message_id) return { label: 'Recebido no WhatsApp', variant: 'neutral' };
  return { label: 'Anexado no CRM', variant: 'neutral' };
}

/**
 * Nome de exibição do documento (F46-S31): `display_name` (organizado pelo
 * agente) tem prioridade sobre `original_filename` (proveniência — o nome
 * como o arquivo chegou do cidadão, nunca sobrescrito). `??` é o operador
 * certo aqui: `display_name` só é `null`/`undefined` quando ausente — nunca
 * string vazia (o servidor normaliza vazio/só-espaço para `null`).
 */
export function resolveDocumentDisplayName(
  doc: Pick<Document, 'display_name' | 'original_filename'>,
): string | null {
  return doc.display_name ?? doc.original_filename;
}

/** `document_type_key` → rótulo de fallback quando o catálogo
 * (GET /api/documents/types — ver api.ts) vem vazio ou falha. */
export function humanizeDocumentTypeKey(key: string): string {
  return key
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Bytes → KB/MB legíveis, 1 casa decimal. */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const SOURCE_LABEL: Record<Document['source'], string> = {
  upload_ui: 'Enviado manualmente',
  whatsapp_inbound: 'Recebido no WhatsApp',
  import: 'Importado',
  api: 'Via integração',
};

const ACTION_BTN = cn(
  'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm font-sans text-xs font-semibold',
  'transition-all duration-fast disabled:opacity-40 disabled:cursor-not-allowed',
  'focus-visible:outline-none focus-visible:ring-2',
);

// ─── Componente ──────────────────────────────────────────────────────────────

export interface DocumentCardProps {
  document: Document;
  /** Rótulo do tipo já resolvido pelo painel (catálogo ou fallback humanizado). */
  typeLabel: string;
  canDownload: boolean;
  canDelete: boolean;
  onDownload: () => void;
  onDelete: () => void;
  /** Abre o DocumentPreviewDrawer — usa a mesma permissão de download. */
  onPreview?: (() => void) | undefined;
  isDownloading?: boolean;
  isDeleting?: boolean;
}

export function DocumentCard({
  document,
  typeLabel,
  canDownload,
  canDelete,
  onDownload,
  onDelete,
  onPreview,
  isDownloading = false,
  isDeleting = false,
}: DocumentCardProps): React.JSX.Element {
  const origin = resolveOriginBadge(document);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>): void => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mx', `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty('--my', `${e.clientY - rect.top}px`);
  };

  return (
    <div
      role="article"
      aria-label={`Documento ${typeLabel}`}
      onMouseMove={handleMouseMove}
      className={cn(
        'relative overflow-hidden rounded-md p-4 flex flex-col gap-3',
        'transition-[transform,box-shadow,border-color] duration-[200ms]',
        'hover:-translate-y-[2px] hover:[border-color:var(--border-strong)]',
        'before:absolute before:inset-0 before:pointer-events-none before:rounded-md',
        'before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-300',
        'before:[background:radial-gradient(200px_circle_at_var(--mx,50%)_var(--my,50%),rgba(27,58,140,0.08),transparent_70%)]',
      )}
      style={{
        background: 'var(--bg-elev-1)',
        border: '1px solid var(--border)',
        boxShadow: 'var(--elev-2)',
        // @ts-expect-error -- CSS custom props não existem em CSSProperties
        '--mx': '50%',
        '--my': '50%',
      }}
    >
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <Badge variant={origin.variant}>{origin.label}</Badge>
        <DocumentStatusBadge document={document} />
      </div>

      <p
        className="font-sans font-semibold"
        style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}
      >
        {typeLabel}
      </p>

      {/* Nome de exibição (display_name ?? original_filename) — mono,
          truncado. Ausente em alguns fluxos de importação. */}
      <p
        className="font-mono truncate"
        title={resolveDocumentDisplayName(document) ?? undefined}
        style={{ fontSize: 'var(--text-xs)', color: 'var(--text-2)' }}
      >
        {resolveDocumentDisplayName(document) ?? '(sem nome de arquivo)'}
      </p>

      <p className="font-sans" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-3)' }}>
        {formatFileSize(document.size_bytes)} · {formatOrgDate(document.created_at)} ·{' '}
        {SOURCE_LABEL[document.source]}
      </p>

      {document.status === 'rejected' && document.rejection_reason && (
        <p
          className="font-sans rounded-sm px-2 py-1.5"
          style={{
            fontSize: 'var(--text-xs)',
            color: 'var(--danger)',
            background: 'var(--danger-bg)',
          }}
        >
          Motivo: {document.rejection_reason}
        </p>
      )}

      {/* Ações — escondidas conforme permissão, nunca assumidas */}
      {(canDownload || canDelete) && (
        <div className="flex items-center gap-2 pt-1 border-t border-border-subtle mt-1">
          {canDownload && onPreview && (
            <button
              type="button"
              onClick={onPreview}
              disabled={document.scan_status === 'rejected'}
              className={cn(
                ACTION_BTN,
                'text-ink-2 hover:bg-surface-hover focus-visible:ring-azul/20',
              )}
            >
              Visualizar
            </button>
          )}
          {canDownload && (
            <button
              type="button"
              onClick={onDownload}
              disabled={isDownloading || document.scan_status === 'rejected'}
              className={cn(
                ACTION_BTN,
                'text-azul hover:bg-azul/10 active:bg-azul/15 focus-visible:ring-azul/20',
              )}
            >
              {isDownloading ? 'Baixando…' : 'Baixar'}
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={onDelete}
              disabled={isDeleting}
              className={cn(
                ACTION_BTN,
                'text-danger hover:bg-danger/10 active:bg-danger/15 focus-visible:ring-danger/20',
              )}
            >
              {isDeleting ? 'Removendo…' : 'Remover'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
